import { getDecimalPlaces } from '@actual-app/core/shared/currencies';
import { convertAmount } from '@actual-app/core/shared/currency';
import type { ExchangeRateEntity } from '@actual-app/core/types/models';

// Pre-indexed rates, grouped by "from:to" pair and sorted ascending by
// date, so a lookup is a binary search instead of a filter+sort over every
// stored rate. Built once per aggregation (see buildRateIndex) rather than
// resolving from the raw array inside a per-account conversion loop: with N
// accounts and R stored rates (e.g. years of daily rates), re-filtering and
// re-sorting the full array per lookup gets expensive fast. Indexing once
// costs O(R log R) total, with each lookup then O(log k) for k = rates on
// that specific pair (almost always far smaller than R, since most budgets
// only ever have one or two currency pairs in play).
export type RateIndex = Map<string, Array<{ rate: number; date: string }>>;

function pairKey(fromCurrency: string, toCurrency: string): string {
  return `${fromCurrency}:${toCurrency}`;
}

export function buildRateIndex(rates: ExchangeRateEntity[]): RateIndex {
  const index: RateIndex = new Map();
  for (const r of rates) {
    // Same validity filter as the underlying resolveRate/findDirectRate
    // logic -- CRDT sync can still land a bad rate outside the normal
    // create/update handlers (see loot-core's shared/currency.ts), so this
    // has to defend itself rather than trust every stored row.
    if (!Number.isFinite(r.rate) || r.rate <= 0) continue;
    const key = pairKey(r.from_currency, r.to_currency);
    let pairRates = index.get(key);
    if (!pairRates) {
      pairRates = [];
      index.set(key, pairRates);
    }
    pairRates.push({ rate: r.rate, date: r.date });
  }
  for (const pairRates of index.values()) {
    pairRates.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }
  return index;
}

// Binary search for the most recent entry on or before the given date.
function findMostRecentIndexed(
  index: RateIndex,
  fromCurrency: string,
  toCurrency: string,
  date: string,
): { rate: number; date: string } | null {
  const pairRates = index.get(pairKey(fromCurrency, toCurrency));
  if (!pairRates || pairRates.length === 0) return null;

  let lo = 0;
  let hi = pairRates.length - 1;
  let result: { rate: number; date: string } | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (pairRates[mid].date <= date) {
      result = pairRates[mid];
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return result;
}

// Same direct-vs-inverse, recency-tie-break semantics as loot-core's
// shared/currency.ts resolveRate, just operating on the pre-built index
// instead of a raw array.
function resolveRateIndexed(
  index: RateIndex,
  fromCurrency: string,
  toCurrency: string,
  date: string,
): number | null {
  const direct = findMostRecentIndexed(index, fromCurrency, toCurrency, date);
  const inverse = findMostRecentIndexed(index, toCurrency, fromCurrency, date);

  if (direct != null && (inverse == null || direct.date >= inverse.date)) {
    return direct.rate;
  }
  if (inverse != null) {
    return 1 / inverse.rate;
  }
  return null;
}

// Converts an account's own balance amount into a target base currency as
// of a specific date, using whichever exchange-rate data is on file (see
// resolveRateIndexed for the direct-vs-inverse/recency rules). Accounts
// with no explicit currency, or whose currency matches the base, need no
// conversion -- this also covers budgets that don't use the currency
// feature at all, since baseCurrency is '' in that case and every
// account's effective currency falls back to '' too, always taking the
// fast no-op path below.
//
// KNOWN LIMITATION: this assumes the account's stored amount actually uses
// accountCurrency's decimal scale. That's only reliably true for the
// account's starting balance (server/accounts/app.ts encodes it with
// amountToCurrencyInteger, i.e. the account's real currency scale) --
// ongoing transactions entered through the desktop transaction editor are
// always stored at a flat 2-decimal scale regardless of account currency
// (table/utils.ts's deserializeTransaction calls amountToInteger with no
// decimalPlaces argument, i.e. always 2). For accounts in a 2-decimal
// currency (the vast majority -- EUR/USD/DKK/GBP/etc.) these scales happen
// to agree, so this is correct in practice today. For a 0- or
// 3-decimal-place currency (JPY, KRW, BHD, ...) the transaction-entry
// pipeline's fixed 2-decimal assumption is itself wrong, independent of
// any aggregation -- fixing it requires making transaction entry
// currency-decimal-aware, a separate, larger piece of work.
//
// KNOWN LIMITATION: an account's `currency` field is a single current
// value with no history -- if it's changed after the account already has
// transactions, this recalculates the account's *entire* historical
// balance stream using the new currency and its rates. This matches the
// account editor's own documented behavior (it already warns that changing
// currency "only relabels the account -- amounts already recorded aren't
// rescaled" -- i.e. the field was never meant to carry effective-dated
// history), so this isn't a new gap, but a proper fix (tracking currency
// changes with an effective date) is a separate feature.
export function tryConvertToBaseCurrency(
  amount: number,
  accountCurrency: string,
  baseCurrency: string,
  date: string,
  rateIndex: RateIndex,
): number | null {
  if (!baseCurrency || accountCurrency === baseCurrency) {
    return amount;
  }

  const rate = resolveRateIndexed(
    rateIndex,
    accountCurrency,
    baseCurrency,
    date,
  );
  return convertAmount(
    amount,
    rate,
    getDecimalPlaces(accountCurrency),
    getDecimalPlaces(baseCurrency),
  );
}

// Same conversion as tryConvertToBaseCurrency, but treats "no rate on file
// yet for this pair/date" as 0 rather than null. Only appropriate for
// *aggregate* sums (Net Worth, cross-account group totals), where "can't
// convert yet" should mean "excluded from the total" -- silently treating
// a foreign-currency balance as if it were already in the base currency
// would show wildly wrong totals (e.g. a DKK balance counted as the same
// number of EUR).
//
// Do not use this for a *single* account's own primary displayed balance:
// showing e.g. "$1,473.12" as a base-currency "€0.00" because no rate is
// on file yet misstates the account as worthless rather than indicating
// conversion is unavailable. Use tryConvertToBaseCurrency directly there
// and render the null case explicitly (e.g. show only the native amount).
export function convertToBaseCurrency(
  amount: number,
  accountCurrency: string,
  baseCurrency: string,
  date: string,
  rateIndex: RateIndex,
): number {
  return (
    tryConvertToBaseCurrency(
      amount,
      accountCurrency,
      baseCurrency,
      date,
      rateIndex,
    ) ?? 0
  );
}
